/**
 * Error codes for the application
 */
export enum ErrorCode {
  // Auth errors
  MISSING_AUTH = 'MISSING_AUTH',
  MISSING_USER_ID = 'MISSING_USER_ID',
  MISSING_ORG = 'MISSING_ORG',
  MISSING_ROLES = 'MISSING_ROLES',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  INVALID_TOKEN = 'INVALID_TOKEN',
  MFA_REQUIRED = 'MFA_REQUIRED',

  // RBAC errors
  ROLE_FORBIDDEN = 'ROLE_FORBIDDEN',
  PERMISSION_NOT_FOUND = 'PERMISSION_NOT_FOUND',
  ROLE_ASSIGNMENT_NOT_FOUND = 'ROLE_ASSIGNMENT_NOT_FOUND',
  LAST_ADMIN_PROTECTED = 'LAST_ADMIN_PROTECTED',
  PROTECTED_ROLE = 'PROTECTED_ROLE',
  MEMBER_NOT_FOUND = 'MEMBER_NOT_FOUND',
  MEMBER_ALREADY_EXISTS = 'MEMBER_ALREADY_EXISTS',

  // Trust Account errors
  TRUST_ACCOUNT_NOT_FOUND = 'TRUST_ACCOUNT_NOT_FOUND',
  TRUST_ACCOUNT_FORBIDDEN = 'TRUST_ACCOUNT_FORBIDDEN',
  TRUST_ACCOUNT_INVALID_STATUS = 'TRUST_ACCOUNT_INVALID_STATUS',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
  TRUST_ACCOUNT_TRUSTEE_ONLY = 'TRUST_ACCOUNT_TRUSTEE_ONLY',
  TRUST_ACCOUNT_MEMBER_NOT_FOUND = 'TRUST_ACCOUNT_MEMBER_NOT_FOUND',
  TRUST_ACCOUNT_MEMBER_ALREADY_EXISTS = 'TRUST_ACCOUNT_MEMBER_ALREADY_EXISTS',
  TRUST_ACCOUNT_MEMBER_NOT_ELIGIBLE = 'TRUST_ACCOUNT_MEMBER_NOT_ELIGIBLE',
  TRUST_ACCOUNT_MEMBER_CHANGED = 'TRUST_ACCOUNT_MEMBER_CHANGED',
  TRUST_ACCOUNT_POWER_OF_ATTORNEY_REQUIRED = 'TRUST_ACCOUNT_POWER_OF_ATTORNEY_REQUIRED',
  GUEST_EMAIL_IS_FIRM_MEMBER = 'GUEST_EMAIL_IS_FIRM_MEMBER',
  EMAIL_IS_GUEST = 'EMAIL_IS_GUEST',
  GUEST_ALREADY_INVITED = 'GUEST_ALREADY_INVITED',
  GUEST_INVITE_REFUSED = 'GUEST_INVITE_REFUSED',
  GUEST_NOT_FOUND = 'GUEST_NOT_FOUND',
  GUEST_ROLE_NOT_ALLOWED = 'GUEST_ROLE_NOT_ALLOWED',
  GUEST_ACCESS_REMOVED = 'GUEST_ACCESS_REMOVED',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  MISSING_ID = 'MISSING_ID',

  // Operation errors
  TRUST_ACCOUNTS_LIST_ERROR = 'TRUST_ACCOUNTS_LIST_ERROR',
  TRUST_ACCOUNTS_CREATE_ERROR = 'TRUST_ACCOUNTS_CREATE_ERROR',
  TRUST_ACCOUNTS_GET_ERROR = 'TRUST_ACCOUNTS_GET_ERROR',
  TRUST_ACCOUNTS_UPDATE_ERROR = 'TRUST_ACCOUNTS_UPDATE_ERROR',
  TRUST_ACCOUNTS_UPSERT_DETAILS_ERROR = 'TRUST_ACCOUNTS_UPSERT_DETAILS_ERROR',

  // Package errors
  PACKAGE_NOT_FOUND = 'PACKAGE_NOT_FOUND',
  PACKAGE_SUBMIT_NOT_ALLOWED = 'PACKAGE_SUBMIT_NOT_ALLOWED',
  INVALID_PACKAGE_TYPE = 'INVALID_PACKAGE_TYPE',
  ACTIVATION_NOT_ALLOWED = 'ACTIVATION_NOT_ALLOWED',

  // Document errors
  DOCUMENT_NOT_FOUND = 'DOCUMENT_NOT_FOUND',
  DOCUMENT_FORBIDDEN = 'DOCUMENT_FORBIDDEN',
  DOCUMENT_UPLOAD_FAILED = 'DOCUMENT_UPLOAD_FAILED',
  DOCUMENT_ALREADY_UPLOADED = 'DOCUMENT_ALREADY_UPLOADED',
  DOCUMENT_CONTENT_REJECTED = 'DOCUMENT_CONTENT_REJECTED',
  PRESIGNED_URL_EXPIRED = 'PRESIGNED_URL_EXPIRED',
  S3_VERIFICATION_FAILED = 'S3_VERIFICATION_FAILED',

  // Purchase errors
  PURCHASE_NOT_FOUND = 'PURCHASE_NOT_FOUND',

  // Buyer errors
  BUYER_NOT_FOUND = 'BUYER_NOT_FOUND',
  BUYER_FORBIDDEN = 'BUYER_FORBIDDEN',
  BUYER_VALIDATION_ERROR = 'BUYER_VALIDATION_ERROR',
  BUYER_ASSET_ALREADY_TAKEN = 'BUYER_ASSET_ALREADY_TAKEN',
  BUYER_ALREADY_ON_PURCHASE = 'BUYER_ALREADY_ON_PURCHASE',
  BUYER_ALREADY_HAS_PURCHASE = 'BUYER_ALREADY_HAS_PURCHASE',
  BUYERS_NOT_CONFIRMED = 'BUYERS_NOT_CONFIRMED',
  BUYER_DELETE_NOT_ALLOWED = 'BUYER_DELETE_NOT_ALLOWED',
  BUYER_IDENTITY_IMMUTABLE = 'BUYER_IDENTITY_IMMUTABLE',
  BUYER_IDENTITY_CHANGE_NOT_APPROVED = 'BUYER_IDENTITY_CHANGE_NOT_APPROVED',
  BUYER_IDENTITY_CONFLICT = 'BUYER_IDENTITY_CONFLICT',

  // Asset errors
  INVALID_ASSET_ATTRIBUTES = 'INVALID_ASSET_ATTRIBUTES',
  ASSET_TYPE_NOT_ALLOWED = 'ASSET_TYPE_NOT_ALLOWED',
  ASSET_LIMIT_EXCEEDED = 'ASSET_LIMIT_EXCEEDED',
  SINGULAR_ASSET_NOT_SUPPORTED = 'SINGULAR_ASSET_NOT_SUPPORTED',
  PURCHASE_ASSET_REQUIRED = 'PURCHASE_ASSET_REQUIRED',
  PURCHASE_LIMIT_EXCEEDED = 'PURCHASE_LIMIT_EXCEEDED',

  // Seller errors
  SELLER_LIMIT_EXCEEDED = 'SELLER_LIMIT_EXCEEDED',
  SELLER_IDENTIFIER_ALREADY_EXISTS = 'SELLER_IDENTIFIER_ALREADY_EXISTS',

  // Beneficiary errors
  BENEFICIARY_NOT_FOUND = 'BENEFICIARY_NOT_FOUND',
  BENEFICIARY_ROLE_NOT_ADDABLE = 'BENEFICIARY_ROLE_NOT_ADDABLE',
  BENEFICIARY_IDENTIFIER_ALREADY_EXISTS = 'BENEFICIARY_IDENTIFIER_ALREADY_EXISTS',
  BENEFICIARY_PARTY_ALREADY_ON_TRUST_ACCOUNT = 'BENEFICIARY_PARTY_ALREADY_ON_TRUST_ACCOUNT',

  // Payment errors
  PAYMENT_INSTRUCTION_NOT_FOUND = 'PAYMENT_INSTRUCTION_NOT_FOUND',
  PAYMENT_FORBIDDEN = 'PAYMENT_FORBIDDEN',
  PAYMENT_LIMIT_EXCEEDED = 'PAYMENT_LIMIT_EXCEEDED',
  PAYMENT_INVALID_STATUS = 'PAYMENT_INVALID_STATUS',
  PAYMENT_VALIDATION_ERROR = 'PAYMENT_VALIDATION_ERROR',
  PAYMENT_PIS_NOT_ALLOWED_FOR_ORGANIZATION = 'PAYMENT_PIS_NOT_ALLOWED_FOR_ORGANIZATION',
  PAYMENT_EXECUTION_FAILED = 'PAYMENT_EXECUTION_FAILED',
  PAYMENT_PURCHASE_NOT_FOUND = 'PAYMENT_PURCHASE_NOT_FOUND',
  PAYMENT_INSTRUCTION_CANNOT_BE_RESENT = 'PAYMENT_INSTRUCTION_CANNOT_BE_RESENT',
  PAYMENT_INSTRUCTION_CANNOT_BE_CANCELLED = 'PAYMENT_INSTRUCTION_CANNOT_BE_CANCELLED',
  PAYMENT_INSTRUCTION_HANDLED_STATE_CONFLICT = 'PAYMENT_INSTRUCTION_HANDLED_STATE_CONFLICT',

  // Payout instruction errors
  PAYOUT_INSTRUCTION_NOT_FOUND = 'PAYOUT_INSTRUCTION_NOT_FOUND',
  PAYOUT_INSTRUCTION_FORBIDDEN = 'PAYOUT_INSTRUCTION_FORBIDDEN',
  PAYOUT_INSTRUCTION_INSUFFICIENT_BALANCE = 'PAYOUT_INSTRUCTION_INSUFFICIENT_BALANCE',
  PAYOUT_INSTRUCTION_AMOUNT_TOO_LOW = 'PAYOUT_INSTRUCTION_AMOUNT_TOO_LOW',
  PAYOUT_INSTRUCTION_TRANSFER_TYPE_NOT_ALLOWED = 'PAYOUT_INSTRUCTION_TRANSFER_TYPE_NOT_ALLOWED',
  PAYOUT_AUTHORITY_CREDITOR_INVALID = 'PAYOUT_AUTHORITY_CREDITOR_INVALID',
  PAYOUT_CREDITOR_MISSING = 'PAYOUT_CREDITOR_MISSING',
  PAYOUT_INSTRUCTION_INITIATION_FAILED = 'PAYOUT_INSTRUCTION_INITIATION_FAILED',
  PAYOUT_INSTRUCTION_INVALID_STATUS = 'PAYOUT_INSTRUCTION_INVALID_STATUS',
  /** Distinct from INVALID_STATUS: a concurrent bank debit won the instruction's single settlement. */
  PAYOUT_INSTRUCTION_ALREADY_SETTLED = 'PAYOUT_INSTRUCTION_ALREADY_SETTLED',
  PAYOUT_INSTRUCTION_CANNOT_BE_CANCELLED = 'PAYOUT_INSTRUCTION_CANNOT_BE_CANCELLED',

  // Reference code errors (shared by payment & payout instructions)
  REFERENCE_CODE_COLLISION = 'REFERENCE_CODE_COLLISION',

  // Payin errors
  PAYIN_NOT_FOUND = 'PAYIN_NOT_FOUND',
  PAYIN_CANNOT_MATCH = 'PAYIN_CANNOT_MATCH',
  PAYIN_MATCH_FAILED = 'PAYIN_MATCH_FAILED',
  PAYIN_NOT_REVIEWABLE = 'PAYIN_NOT_REVIEWABLE',
  TRUST_ACCOUNT_RESOLUTION_FAILED = 'TRUST_ACCOUNT_RESOLUTION_FAILED',

  // Bank credit errors
  BANK_CREDIT_NOT_FOUND = 'BANK_CREDIT_NOT_FOUND',
  BANK_CREDIT_ALREADY_RESOLVED = 'BANK_CREDIT_ALREADY_RESOLVED',
  BANK_CREDIT_BANK_MISMATCH = 'BANK_CREDIT_BANK_MISMATCH',

  // Bank debit errors
  BANK_DEBIT_NOT_FOUND = 'BANK_DEBIT_NOT_FOUND',
  BANK_DEBIT_ALREADY_RESOLVED = 'BANK_DEBIT_ALREADY_RESOLVED',
  BANK_DEBIT_BANK_MISMATCH = 'BANK_DEBIT_BANK_MISMATCH',

  // Bank line classification errors
  BANK_LINE_CLASSIFICATION_INVALID = 'BANK_LINE_CLASSIFICATION_INVALID',
  BANK_LINE_UNKNOWN_BANK = 'BANK_LINE_UNKNOWN_BANK',
  BANK_LINE_LEDGER_BOOKING_CONFLICT = 'BANK_LINE_LEDGER_BOOKING_CONFLICT',

  // Match run errors
  MATCH_RUN_ALREADY_IN_PROGRESS = 'MATCH_RUN_ALREADY_IN_PROGRESS',
  CONNECTION_REFRESH_FAILED = 'CONNECTION_REFRESH_FAILED',

  // Webhook errors
  WEBHOOK_EVENT_NOT_FOUND = 'WEBHOOK_EVENT_NOT_FOUND',
  WEBHOOK_AUTHENTICATION_FAILED = 'WEBHOOK_AUTHENTICATION_FAILED',
  WEBHOOK_SOURCE_NOT_AUTHORIZED = 'WEBHOOK_SOURCE_NOT_AUTHORIZED',
  WEBHOOK_PAYLOAD_INVALID = 'WEBHOOK_PAYLOAD_INVALID',
  WEBHOOK_DUPLICATE_EVENT = 'WEBHOOK_DUPLICATE_EVENT',

  // Extraction errors
  EXTRACTION_RUN_NOT_FOUND = 'EXTRACTION_RUN_NOT_FOUND',
  EXTRACTION_RUN_FORBIDDEN = 'EXTRACTION_RUN_FORBIDDEN',
  EXTRACTION_RUN_ALREADY_PROCESSING = 'EXTRACTION_RUN_ALREADY_PROCESSING',
  EXTRACTION_FAILED = 'EXTRACTION_FAILED',
  EXTRACTION_UNSUPPORTED_DOCUMENT_TYPE = 'EXTRACTION_UNSUPPORTED_DOCUMENT_TYPE',

  // FATCA-CRS errors
  FATCA_CRS_MAIN_DECLARATION_REQUIRED = 'FATCA_CRS_MAIN_DECLARATION_REQUIRED',
  FATCA_CRS_MAIN_DECLARATION_NOT_SIGNED = 'FATCA_CRS_MAIN_DECLARATION_NOT_SIGNED',
  FATCA_CRS_SELLER_DETAILS_REQUIRED = 'FATCA_CRS_SELLER_DETAILS_REQUIRED',
  FATCA_CRS_SUPPLEMENTARY_FORM_INCOMPLETE = 'FATCA_CRS_SUPPLEMENTARY_FORM_INCOMPLETE',
  FATCA_CRS_SELLER_ROLE_REQUIRED = 'FATCA_CRS_SELLER_ROLE_REQUIRED',

  // Payment link errors
  PAYMENT_LINK_NOT_FOUND = 'PAYMENT_LINK_NOT_FOUND',
  PAYMENT_LINK_INACTIVE = 'PAYMENT_LINK_INACTIVE',

  // Pay session errors (phase-2 security hardening)
  PAY_SESSION_INVALID = 'PAY_SESSION_INVALID',
  PAY_SESSION_EXPIRED = 'PAY_SESSION_EXPIRED',
  PAY_RATE_LIMITED = 'PAY_RATE_LIMITED',

  // Verification code / step-up errors (generic MFA guard, reused across sensitive actions)
  VERIFICATION_CODE_INVALID = 'VERIFICATION_CODE_INVALID',
  VERIFICATION_CODE_EXPIRED = 'VERIFICATION_CODE_EXPIRED',
  VERIFICATION_CODE_ATTEMPTS_EXCEEDED = 'VERIFICATION_CODE_ATTEMPTS_EXCEEDED',
  STEP_UP_RATE_LIMITED = 'STEP_UP_RATE_LIMITED',

  // Initiate-payment failure reasons (curated from OF's documented error catalog)
  PAY_PROVIDER_UNAVAILABLE = 'PAY_PROVIDER_UNAVAILABLE',
  PAY_PROVIDER_ERROR = 'PAY_PROVIDER_ERROR',
  PAY_UNEXPECTED_SERVER_ERROR = 'PAY_UNEXPECTED_SERVER_ERROR',
  PAY_BRANCH_NOT_SUPPORTED = 'PAY_BRANCH_NOT_SUPPORTED',
  PAY_INVALID_PSU_ID = 'PAY_INVALID_PSU_ID',
  PAY_INVALID_OF_TOKEN = 'PAY_INVALID_OF_TOKEN',
  PAY_MISSING_PAYMENT_INFO = 'PAY_MISSING_PAYMENT_INFO',
  PAY_INVALID_PROVIDER = 'PAY_INVALID_PROVIDER',
  PAY_MISSING_ACCOUNT_INFO = 'PAY_MISSING_ACCOUNT_INFO',
  PAY_REDIRECT_URL_NOT_ALLOWED = 'PAY_REDIRECT_URL_NOT_ALLOWED',
  PAY_ACCOUNTS_MATCH_ERROR = 'PAY_ACCOUNTS_MATCH_ERROR',
  PAY_PROVIDER_MISMATCH = 'PAY_PROVIDER_MISMATCH',
  PAY_MERCHANT_INFO_REQUIRED = 'PAY_MERCHANT_INFO_REQUIRED',
  PAY_DESCRIPTION_MISSING = 'PAY_DESCRIPTION_MISSING',
  PAY_SANDBOX_ACCOUNT_REQUIRED = 'PAY_SANDBOX_ACCOUNT_REQUIRED',
  PAY_PROVIDER_NOT_FOUND = 'PAY_PROVIDER_NOT_FOUND',
  PAY_MERCHANT_NOT_FOUND = 'PAY_MERCHANT_NOT_FOUND',
  PAY_CREDITOR_ACCOUNT_NOT_ALLOWED = 'PAY_CREDITOR_ACCOUNT_NOT_ALLOWED',
  PAY_PROVIDER_NOT_ALLOWED = 'PAY_PROVIDER_NOT_ALLOWED',
  PAY_ZAHAV_CLOSED = 'PAY_ZAHAV_CLOSED',
  PAY_PAYMENT_IN_PROGRESS = 'PAY_PAYMENT_IN_PROGRESS',
  PAY_OF_UNAUTHORIZED = 'PAY_OF_UNAUTHORIZED',
  PAY_ORG_ID_MISSING = 'PAY_ORG_ID_MISSING',
  PAY_ORG_SETTINGS_NOT_FOUND = 'PAY_ORG_SETTINGS_NOT_FOUND',
  PAY_PAYMENT_CREATION_ERROR = 'PAY_PAYMENT_CREATION_ERROR',
  PAY_CONSENT_CREATION_ERROR = 'PAY_CONSENT_CREATION_ERROR',

  // User profile errors
  AVATAR_TOO_LARGE = 'AVATAR_TOO_LARGE',
  UNSUPPORTED_AVATAR_TYPE = 'UNSUPPORTED_AVATAR_TYPE',

  // Firm profile errors
  LOGO_TOO_LARGE = 'LOGO_TOO_LARGE',
  UNSUPPORTED_LOGO_TYPE = 'UNSUPPORTED_LOGO_TYPE',

  // Organization (ops) errors
  ORG_SLUG_TAKEN = 'ORG_SLUG_TAKEN',
  ORG_NOT_EMPTY = 'ORG_NOT_EMPTY',

  // General errors
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  INVALID_INPUT = 'INVALID_INPUT',
  INTERNAL_ERROR = 'INTERNAL_ERROR'
}
